/**
 * Search Bar Component with City Selector
 * Combined input with city dropdown, geolocation, and profile city support
 * Token-only colors
 * 
 * IMPORTANT: Uses useAuth() hook to read cached auth state.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, X, Search, Check, Locate, Loader2, BookmarkPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { profileService, type ProfileCity } from '@/lib/services/profileService';
import { logger } from '@/lib/services';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeInputSingleLine } from '@/lib/validation/inputLimits';

import cancelIcon from '@/assets/cancel.png';

const MAX_SEARCH_LENGTH = 100;

interface City {
  id: string;
  country_code: string;
  name: string;
  region_name: string | null;
  popularity: number;
  is_other: boolean;
  is_active: boolean;
  sort_order: number;
  parent_id: string | null;
  distance_km?: number;
}

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  isSticky?: boolean;
  selectedCityId?: string | null;
  selectedCityName?: string;
  onCitySelect?: (city: { id: string; name: string } | null) => void;
  onSaveFilters?: () => void;
  canSaveFilters?: boolean;
  isSavingFilters?: boolean;
}

type GeolocationState = "idle" | "loading" | "success" | "error" | "denied";

const SearchBar = ({
  value,
  onChange,
  placeholder = 'Друг где-то поблизости',
  debounceMs = 500,
  isSticky = false,
  selectedCityId,
  selectedCityName,
  onCitySelect,
  onSaveFilters,
  canSaveFilters,
  isSavingFilters,
}: SearchBarProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [nearbyCities, setNearbyCities] = useState<City[]>([]);
  const [profileCity, setProfileCity] = useState<ProfileCity | null>(null);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [geoState, setGeoState] = useState<GeolocationState>("idle");
  const [geoSupported] = useState(() => "geolocation" in navigator);
  const [pendingSaveCity, setPendingSaveCity] = useState<{ id: string; name: string } | null>(null);
  
  // Get user from cached auth state — no network call!
  const { user } = useAuth();
  const userId = user?.id ?? null;
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Sync external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Load user's profile city on mount
  useEffect(() => {
    const loadProfileCity = async () => {
      if (userId) {
        const { data } = await profileService.getProfileCity(userId);
        if (data) {
          setProfileCity(data);
        }
      }
    };
    loadProfileCity();
  }, [userId]);

  // City fetching
  const fetchCities = useCallback(async (searchQuery: string) => {
    setIsCityLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_cities', {
        p_country_code: 'RU',
        p_query: searchQuery || null,
        p_limit: 50,
        p_offset: 0,
        p_include_inactive: false,
        p_include_other: true,
        p_require_coords: true,
      });

      if (error) {
        logger.error('Error fetching cities', {
          context: { feature: 'geo', function_name: 'fetchCities' },
          error,
          error_code: 'cities_fetch_failed',
          error_type: 'unexpected',
        });
        setCities([]);
      } else {
        let sortedCities = (data as City[]) || [];
        
        // If profile city exists and not searching, put it first
        if (profileCity && !searchQuery) {
          sortedCities = sortedCities.filter(c => c.id !== profileCity.id);
        }
        
        // Sort: Moscow first, then St. Petersburg, then rest
        sortedCities = sortedCities.sort((a, b) => {
          const aIsMoscow = a.name.toLowerCase().includes('москва');
          const bIsMoscow = b.name.toLowerCase().includes('москва');
          const aIsPiter = a.name.toLowerCase().includes('санкт-петербург') || a.name.toLowerCase().includes('петербург');
          const bIsPiter = b.name.toLowerCase().includes('санкт-петербург') || b.name.toLowerCase().includes('петербург');
          
          if (aIsMoscow && !bIsMoscow) return -1;
          if (!aIsMoscow && bIsMoscow) return 1;
          if (aIsPiter && !bIsPiter) return -1;
          if (!aIsPiter && bIsPiter) return 1;
          return 0;
        });
        setCities(sortedCities);
      }
    } catch (err) {
      logger.error('Error fetching cities', {
        context: { feature: 'geo', function_name: 'fetchCities' },
        error: err as Error,
        error_code: 'cities_fetch_failed',
        error_type: 'unexpected',
      });
      setCities([]);
    } finally {
      setIsCityLoading(false);
    }
  }, [profileCity]);

  // Fetch nearby cities based on coordinates
  const fetchNearbyCities = useCallback(async (lat: number, lng: number) => {
    try {
      const { data, error } = await supabase.rpc("get_cities_nearby", {
        p_lat: lat,
        p_lng: lng,
        p_country_code: "RU",
        p_limit: 5,
        p_radius_km: 100,
      });

      if (error) {
        logger.error("Error fetching nearby cities", {
          context: { feature: 'geo', function_name: 'fetchNearbyCities' },
          error,
          error_code: 'nearby_cities_fetch_failed',
          error_type: 'unexpected',
        });
        return [];
      }
      return (data as City[]) || [];
    } catch (err) {
      logger.error("Error fetching nearby cities", {
        context: { feature: 'geo', function_name: 'fetchNearbyCities' },
        error: err as Error,
        error_code: 'nearby_cities_fetch_failed',
        error_type: 'unexpected',
      });
      return [];
    }
  }, []);

  // Handle geolocation request
  const handleGetLocation = useCallback(async () => {
    if (!geoSupported) return;
    
    setGeoState("loading");
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const nearby = await fetchNearbyCities(latitude, longitude);
        
        if (nearby.length > 0) {
          setNearbyCities(nearby);
          setGeoState("success");
          // Auto-select the closest city
          const closestCity = nearby[0];
          onCitySelect?.({ id: closestCity.id, name: closestCity.name });
          setIsDropdownOpen(false);
          
          // If user is logged in and this is not their profile city, offer to save
          if (userId && profileCity?.id !== closestCity.id) {
            setPendingSaveCity({ id: closestCity.id, name: closestCity.name });
            toast(
              `Сохранить "${closestCity.name}" как ваш город?`,
              {
                action: {
                  label: 'Сохранить',
                  onClick: () => handleSaveCityToProfile(closestCity.id, closestCity.name),
                },
                duration: 8000,
              }
            );
          }
        } else {
          setGeoState("error");
          setNearbyCities([]);
        }
      },
      (error) => {
        logger.warn("Geolocation error", {
          context: { feature: 'geo', function_name: 'handleGetLocation' },
          data: { code: error.code, message: error.message },
        });
        if (error.code === error.PERMISSION_DENIED) {
          setGeoState("denied");
        } else {
          setGeoState("error");
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    );
  }, [geoSupported, fetchNearbyCities, onCitySelect, userId, profileCity]);

  // Save city to profile
  const handleSaveCityToProfile = async (cityId: string, cityName: string) => {
    if (!userId) return;
    
    const { success, error } = await profileService.saveCity(userId, cityId);
    if (success) {
      setProfileCity({ id: cityId, name: cityName });
      setPendingSaveCity(null);
      toast.success(`Город "${cityName}" сохранён в профиле`);
    } else {
      logger.error('Failed to save city', {
        context: { feature: 'profile', function_name: 'handleSaveCityToProfile' },
        error,
        error_code: 'city_save_failed',
        error_type: 'unexpected',
      });
      toast.error('Не удалось сохранить город');
    }
  };

  // Debounced search for cities
  useEffect(() => {
    if (isDropdownOpen) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        fetchCities(localValue);
      }, 200);
      return () => {
        if (debounceTimer.current) {
          clearTimeout(debounceTimer.current);
        }
      };
    }
  }, [isDropdownOpen, localValue, fetchCities]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeInputSingleLine(e.target.value).slice(0, MAX_SEARCH_LENGTH);
    setLocalValue(sanitized);
    onChange(sanitized);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
    fetchCities(localValue);
  };

  const handleClear = () => {
    setLocalValue('');
    onChange('');
    onCitySelect?.(null);
    inputRef.current?.focus();
  };

  const handleCitySelect = (city: City | ProfileCity) => {
    onCitySelect?.({ id: city.id, name: city.name });
    setLocalValue('');
    onChange('');
    setIsDropdownOpen(false);
  };

  // Hide keyboard on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (document.activeElement === inputRef.current) {
        inputRef.current?.blur();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const displayValue = selectedCityName || localValue;

  const getGeoButtonText = () => {
    switch (geoState) {
      case "loading":
        return "Определяем...";
      case "denied":
        return "Доступ запрещён";
      case "error":
        return "Не удалось определить";
      default:
        return "Определить автоматически";
    }
  };

  return (
    <div
      data-testid="tab-001-search"
      className={`w-full relative z-30 ${isSticky ? 'sticky top-0 z-40' : ''}`}
    >
      <div className="flex items-center gap-2">
        {/* Main search/city field */}
        <div ref={containerRef} className="relative flex-1">
          <div
            className="flex items-center gap-2 bg-bg-secondary h-[52px] px-3"
            style={{
              borderRadius: '16px',
              fontFamily: '"CoFo Gothic", system-ui, sans-serif',
            }}
          >
            {/* Location icon */}
            <MapPin className="w-5 h-5 flex-shrink-0 text-text-primary/70" />

            {/* Input or selected city display */}
            {selectedCityName ? (
              <div className="flex-1 flex items-center gap-2">
                <span className="text-text-primary text-[16px]">{selectedCityName}</span>
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                value={localValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={placeholder}
                className="flex-1 min-w-0 bg-transparent text-text-primary text-[16px] placeholder:text-text-primary/50 outline-none"
                style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
              />
            )}

            {/* Clear button */}
            {(localValue.length > 0 || selectedCityName) && (
              <button
                onClick={handleClear}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center"
              >
                <img
                  src={cancelIcon}
                  alt="Очистить"
                  className="w-4 h-4 opacity-70"
                />
              </button>
            )}
          </div>

          {/* City dropdown */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-bg-primary rounded-xl shadow-lg border border-border-tertiary z-50 overflow-hidden">
              {/* Geolocation button */}
              {geoSupported && (
                <button
                  type="button"
                  onClick={handleGetLocation}
                  disabled={geoState === "loading"}
                  className={cn(
                    "w-full px-4 py-3 text-left text-sm flex items-center gap-3 border-b border-border-tertiary transition-colors",
                    geoState === "loading" 
                      ? "bg-bg-secondary text-text-tertiary cursor-wait"
                      : geoState === "denied" || geoState === "error"
                        ? "bg-bg-secondary text-text-tertiary"
                        : "hover:bg-bg-accent-pink text-text-accent-pink"
                  )}
                >
                  {geoState === "loading" ? (
                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  ) : (
                    <Locate className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span>{getGeoButtonText()}</span>
                </button>
              )}

              {/* Profile city (saved city) - show first if exists */}
              {profileCity && !localValue && (
                <div className="border-b border-border-tertiary">
                  <div className="px-4 py-2 text-xs text-text-tertiary uppercase tracking-wide bg-bg-secondary flex items-center gap-1">
                    <BookmarkPlus className="w-3 h-3" />
                    Ваш город
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCitySelect(profileCity)}
                    className={cn(
                      "w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors",
                      selectedCityId === profileCity.id && "bg-bg-accent-pink"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <MapPin className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                      <div className="flex flex-col">
                        <span className="text-text-primary font-medium">{profileCity.name}</span>
                        {profileCity.region_name && (
                          <span className="text-xs text-text-tertiary">{profileCity.region_name}</span>
                        )}
                      </div>
                    </div>
                    {selectedCityId === profileCity.id && (
                      <Check className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                    )}
                  </button>
                </div>
              )}

              {/* Nearby cities (if found) */}
              {nearbyCities.length > 0 && !localValue && (
                <div className="border-b border-border-tertiary">
                  <div className="px-4 py-2 text-xs text-text-tertiary uppercase tracking-wide bg-bg-secondary">
                    Рядом с вами
                  </div>
                  {nearbyCities.filter(c => c.id !== profileCity?.id).map((city) => (
                    <button
                      key={`nearby-${city.id}`}
                      type="button"
                      onClick={() => handleCitySelect(city)}
                      className={cn(
                        "w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors",
                        selectedCityId === city.id && "bg-bg-accent-pink"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                        <div className="flex flex-col">
                          <span className="text-text-primary">{city.name}</span>
                          <span className="text-xs text-text-tertiary">
                            {city.region_name}
                            {city.distance_km !== undefined && ` • ${city.distance_km} км`}
                          </span>
                        </div>
                      </div>
                      {selectedCityId === city.id && (
                        <Check className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}

              <div className="max-h-72 overflow-y-auto">
                {isCityLoading ? (
                  <div className="px-4 py-3 text-center text-sm text-text-tertiary">
                    Загрузка...
                  </div>
                ) : cities.length === 0 ? (
                  <div className="px-4 py-3 text-center text-sm text-text-tertiary">
                    {localValue ? 'Ничего не найдено' : 'Начните вводить город'}
                  </div>
                ) : (
                  cities.map((city) => (
                    <button
                      key={city.id}
                      type="button"
                      onClick={() => handleCitySelect(city)}
                      className={cn(
                        'w-full px-4 py-3 text-left text-sm flex items-center justify-between hover:bg-bg-secondary transition-colors',
                        selectedCityId === city.id && 'bg-bg-accent-pink'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                        <div className="flex flex-col">
                          <span className="text-text-primary">{city.name}</span>
                          {city.region_name && (
                            <span className="text-xs text-text-tertiary">{city.region_name}</span>
                          )}
                        </div>
                      </div>
                      {selectedCityId === city.id && (
                        <Check className="w-4 h-4 text-text-accent-pink flex-shrink-0" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Save filters to favorites */}
        <button
          className="flex items-center justify-center h-[52px] w-[52px] bg-bg-accent-pink-secondary rounded-2xl flex-shrink-0 transition-colors active:opacity-80 disabled:opacity-50"
          onClick={onSaveFilters}
          disabled={!canSaveFilters || isSavingFilters}
          title="Сохранить фильтры"
        >
          <BookmarkPlus className="w-5 h-5 text-text-accent-pink" />
        </button>
      </div>
    </div>
  );
};

export default SearchBar;